import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import { PRODUCT_CATEGORIES_FEATURE } from "constants/feature-flags";
import { useGetProductCategoriesQuery } from "services/product-categories";
import { ProductCategory } from "services/types/product-categories.type";

const NO_CATEGORIES: ProductCategory[] = [];

export type ProductCategories = {
  isEnabled: boolean;
  categoriesById: Map<number, ProductCategory>;
  // Every category, archived included, in the practice's order.
  categories: ProductCategory[];
  // Unarchived, in the practice's order: what a filter or an assignment offers.
  activeCategories: ProductCategory[];
  getCategory: (id: number | null | undefined) => ProductCategory | undefined;
};

// Products carry only a category id. Every surface resolves it through this
// one cached list, so a rename or reorder shows everywhere on its next render.
export default function useProductCategories(): ProductCategories {
  const flags = useFlags();
  const isEnabled = Boolean(flags[PRODUCT_CATEGORIES_FEATURE]);
  const { data } = useGetProductCategoriesQuery(
    { include_archived: true },
    { skip: !isEnabled }
  );
  // `skip` keeps the last response, so the flag gates the data as well.
  const categories = isEnabled && data ? data : NO_CATEGORIES;

  return useMemo(() => {
    const categoriesById = new Map(
      categories.map((category) => [category.id, category])
    );
    return {
      isEnabled,
      categoriesById,
      categories,
      activeCategories: categories.filter((category) => !category.is_archived),
      getCategory: (id) => (id == null ? undefined : categoriesById.get(id))
    };
  }, [categories, isEnabled]);
}
